import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const regions = [
  { code: 'eu', name: 'Europe', domain: 'eu.nexuscms.ai', flag: '🇪🇺', city: 'Frankfurt, Germany' },
  { code: 'us', name: 'United States', domain: 'us.nexuscms.ai', flag: '🇺🇸', city: 'New York, USA' },
  { code: 'ap', name: 'Asia Pacific', domain: 'ap.nexuscms.ai', flag: '🌏', city: 'Singapore' },
  { code: 'au', name: 'Australia', domain: 'au.nexuscms.ai', flag: '🇦🇺', city: 'Sydney, Australia' },
];

const RegionSelector = () => {
  const recommended = useMemo(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (tz.includes('Europe') || tz.includes('Africa')) return 'eu';
      if (tz.includes('Australia')) return 'au';
      if (tz.includes('Asia') || tz.includes('Pacific')) return 'ap';
      if (tz.includes('America')) return 'us';
    } catch (_) { /* ignore */ }
    return 'us';
  }, []);

  const [bgLoaded, setBgLoaded] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBgLoaded(true);
    img.onerror = () => setBgLoaded(true);
    img.src = '/region-bg.jpg';
  }, []);

  const handleRegionSelect = (domain) => {
    window.location.href = `https://${domain}`;
  };

  const recommendedName = regions.find((r) => r.code === recommended)?.name || '';

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>Choose your region | Nexus CMS</title>
      </Helmet>
      {/* Decorative blurred background - NOT interactive */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-0 pointer-events-none select-none transition-opacity duration-500"
        style={{
          backgroundImage: bgLoaded ? "url('/region-bg.jpg')" : 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(8px) saturate(1.05)',
          transform: 'scale(1.05)',
          opacity: bgLoaded ? 1 : 0.85,
        }}
      />
      {/* Dark gradient overlay to keep modal legible regardless of background contents */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(15,23,42,0.55) 0%, rgba(30,27,75,0.45) 50%, rgba(15,23,42,0.55) 100%)',
        }}
      />

      {/* Modal container */}
      <div className="relative z-[2] min-h-screen flex items-center justify-center p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="region-title"
          className="w-full max-w-[640px] bg-white/95 border border-white/40 rounded-3xl shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)] backdrop-blur-xl backdrop-saturate-150 px-9 pt-10 pb-8 animate-[popIn_0.4s_cubic-bezier(0.16,1,0.3,1)_both]"
          style={{ animation: 'popIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both' }}
        >
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-7">
            <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-600/30">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                <path d="M3 9h18"></path>
                <path d="M9 21V9"></path>
              </svg>
            </div>
            <div className="text-[22px] font-bold text-slate-900 tracking-tight">Nexus CMS</div>
          </div>

          <h1
            id="region-title"
            className="text-[1.75rem] sm:text-3xl font-bold text-slate-900 text-center mb-2 tracking-tight leading-tight"
          >
            Select Your Region
          </h1>
          <p className="text-[15px] text-slate-500 text-center mb-6 max-w-md mx-auto">
            Choose your nearest data center for the best performance.
          </p>

          {recommendedName && (
            <div className="mx-auto mb-5 max-w-sm rounded-xl border border-indigo-200 bg-indigo-50/50 px-4 py-2.5 text-center">
              <span className="text-[13px] text-slate-600 font-medium">
                Recommended for you: <strong className="text-indigo-600 font-semibold">{recommendedName}</strong>
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {regions.map((region) => {
              const isRecommended = region.code === recommended;
              return (
                <button
                  key={region.code}
                  onClick={() => handleRegionSelect(region.domain)}
                  data-testid={`region-${region.code}`}
                  className={`relative text-left rounded-2xl border bg-white px-4 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-12px_rgba(79,70,229,0.35)] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    isRecommended
                      ? 'border-indigo-600 bg-indigo-50/40 shadow-[0_0_0_3px_rgba(79,70,229,0.12)]'
                      : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/30'
                  }`}
                >
                  {isRecommended && (
                    <span className="absolute -top-2.5 right-3 inline-flex items-center rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                      Recommended
                    </span>
                  )}
                  <div className="text-[28px] leading-none mb-1.5">{region.flag}</div>
                  <div className="font-semibold text-[15px] text-slate-900">{region.name}</div>
                  <div className="text-[12.5px] text-slate-500">{region.city}</div>
                </button>
              );
            })}
          </div>

          <p className="text-center text-[12px] text-slate-500">
            Your data will be stored in the region you choose.
          </p>
          <p className="text-center text-[11px] text-slate-400 mt-4">
            © {new Date().getFullYear()} Nexus CMS. All rights reserved.
          </p>
        </div>
      </div>

      {/* Keyframe animation (Tailwind doesn't ship this preset by default) */}
      <style>{`
        @keyframes popIn {
          from { opacity: 0; transform: translateY(12px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default RegionSelector;
